'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from 'sonner';
import { getOrCreateDefaultAgentDirectMessage } from '~/src/app/(application)/[organization_id]/messages/actions';
import { getHref, ROUTE } from '@utils/route-utils';

/**
 * Shared by both the desktop and mobile "AI chat" top-bar triggers: always
 * resolves to a single DM with the organization's agent (auto-provisioning
 * one on first use), then navigates there. Visibility is each trigger's own
 * concern (via `useAgentsMembershipEnabled()`) -- this hook only handles the
 * navigation/loading/error side once a trigger has decided to render.
 */
export function useStartAgentChat(organizationId: string) {
  const router = useRouter();
  const t = useTranslations('common');
  const [loading, setLoading] = useState(false);

  const start = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { channelId } = await getOrCreateDefaultAgentDirectMessage({
        organizationId
      });
      router.push(getHref(organizationId, [ROUTE.MESSAGES, channelId]));
    } catch {
      toast.error(t('ai-chat-error'));
    } finally {
      setLoading(false);
    }
  };

  return { start, loading };
}
